import React, {useEffect} from 'react'
import {Row, Col, InlineLoader} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {PublicRegisteredCompaniesRetrievalStatus} from '@modifi/enums'

import DynamicEntriesTable, {DynamicEntry} from '../../../../component/dynamic-entries-table'
import SummaryPageFooter from '../../components/summary-page-footer'
import {SECTION_LEGAL_REPRESENTATIVES} from '../../../../constants/sections'
import {FormContentWrapper} from '../../../../ui/form'
import {StyledImg, ImageContainer} from '../../../summary-page-image/styles'
import {StyledParagraph} from '../../../../ui/styles'
import DataSourceWizard from '../../../intro/data-source-wizard'
import SummaryPageHeader from '../../components/summary-page-header'

const tableHeadlines = ['name', 'position']

/** One row of the LegalRepSummaryPage table. */
type LegalRepSummaryEntry = DynamicEntry & {
  route: string
  name: string
  position: string
}

export type LegalRepSummaryPageProps = {
  isLoading: boolean
  legalRepSummary: LegalRepSummaryEntry[]
  isReadonly: boolean
  createLegalRep: () => void
  fetchResolvedCompanyDetails: () => void
  hasLegalRepsFromProvider?: boolean
  retrievalStatus?: string
  fetchPublicRegisteredCompanies: () => void
}

const LegalRepSummaryPage: React.FC<LegalRepSummaryPageProps> = ({
  isLoading,
  legalRepSummary,
  isReadonly,
  createLegalRep,
  fetchResolvedCompanyDetails,
  hasLegalRepsFromProvider = false,
  retrievalStatus = PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION,
  fetchPublicRegisteredCompanies,
}) => {
  const {t} = useTranslation('charon')

  const legalRepCount = legalRepSummary.length

  useEffect(() => {
    fetchPublicRegisteredCompanies()
  }, [fetchPublicRegisteredCompanies])

  useEffect(() => {
    if (
      !isLoading &&
      legalRepCount < 1 &&
      retrievalStatus === PublicRegisteredCompaniesRetrievalStatus.COMPLETED
    ) {
      fetchResolvedCompanyDetails()
    }
  }, [fetchResolvedCompanyDetails, legalRepCount, isLoading, retrievalStatus])

  let content
  let showAddLegalRepButton = false
  if (isLoading) {
    content = (
      <Row>
        <Col>
          <InlineLoader />
        </Col>
      </Row>
    )
  } else if (legalRepCount < 1) {
    if (hasLegalRepsFromProvider) {
      showAddLegalRepButton = false
      content = <DataSourceWizard personRoleType={SECTION_LEGAL_REPRESENTATIVES} />
    } else {
      showAddLegalRepButton = true
      content = (
        <>
          <Row justifyContent="center" data-ident="legal-rep-page.no-persons.image">
            <Col auto>
              <ImageContainer>
                <StyledImg src="/assets/images/no-results-dynamic-entries.png" alt="no-results" />
              </ImageContainer>
            </Col>
          </Row>
          <Row justifyContent="center">
            <Col auto>
              <StyledParagraph textAlign="center">
                {t('legal-rep-summary-page.no-results.message')}
              </StyledParagraph>
            </Col>
          </Row>
        </>
      )
    }
  } else {
    showAddLegalRepButton = true
    content = (
      <Row mb={40} data-ident="legal-rep-summary-table">
        <Col>
          <DynamicEntriesTable
            dataList={legalRepSummary || []}
            headlines={tableHeadlines}
            transPrefix="legal-rep-summary"
          />
        </Col>
      </Row>
    )
  }

  return (
    <>
      <FormContentWrapper>
        <SummaryPageHeader
          ctaText={t(`sub-section-menu.${SECTION_LEGAL_REPRESENTATIVES}.create`)}
          onCreate={createLegalRep}
          isDisabled={isReadonly}
          hideCta={!showAddLegalRepButton}
        />
        {content}
      </FormContentWrapper>

      <SummaryPageFooter sectionName={SECTION_LEGAL_REPRESENTATIVES} />
    </>
  )
}

export default LegalRepSummaryPage
